import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Stack } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import AttachmentIcon from '@mui/icons-material/Attachment';

import JmEasyReasonDialog from './JmEasyReasonDialog';
import JmEasyQuantityDialog from './JmEasyQuantityDialog';
import { NotificationType } from '../../../config';
import addNotification from '../../../utils/addNotification';
import colorFinder from '../../../utils/colorFinder';
import ProductionReceiptDialog from './ProductionReceiptDialog';
import jobmonitorEasyService from '../../../service/jobMonitorEasy.service';
import useParam from '../../../context/ParamContext';
import { JmEasyActionMode } from '../../../config/jobmonitorEasy';

dayjs.extend(relativeTime);
dayjs.extend(duration);

const InfoRow = ({ label, children, valueSx, mb = 1.5 }) => (
  <Typography fontSize={20} sx={{ mb, wordBreak: 'break-word' }}>
    <Box component="span" fontWeight="bold">
      {label}{' '}
    </Box>

    <Box component="span" sx={valueSx}>
      {children}
    </Box>
  </Typography>
);

const JmEasyJobmonitorCard = ({
  data,
  userId,
  setRefetch,
  helperData,
  reasons,
  autoCompleteOptions,
  getAttachments,
  setConfirmDialogData,
  hideConfirmDialog,
}) => {
  const { params } = useParam();
  const [elapsedTime, setElapsedTime] = useState(null);
  const [timer, setTimer] = useState(null);
  const [quantityDialogData, setQuantityDialogData] = useState({
    open: false,
    mode: null,
    reasonCode: null,
  });
  const [reasonDialogData, setReasonDialogData] = useState({
    open: false,
    mode: null,
  });

  useEffect(() => {
    if (data.productionreportStartDate) {
      if (timer) {
        clearInterval(timer);
      }

      const interval = setInterval(() => {
        const start = dayjs(data.productionreportStartDate);
        const now = dayjs();
        const diff = dayjs.duration(now.diff(start));

        setElapsedTime(
          `${diff.hours().toString().padStart(2, '0')}:${diff.minutes().toString().padStart(2, '0')}:${diff.seconds().toString().padStart(2, '0')}`
        );
      }, 1000);

      setTimer(interval);

      return () => clearInterval(interval);
    } else {
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
      setElapsedTime(null);
    }
  }, [data.productionreportStartDate]);

  const openQuantityDialog = (mode, reasonCode = null) => {
    setQuantityDialogData({
      open: true,
      mode,
      reasonCode,
    });
  };

  const closeQuantityDialog = () => {
    setQuantityDialogData({
      open: false,
      mode: null,
      reasonCode: null,
    });
  };

  const openReasonDialog = (mode) => {
    setReasonDialogData({
      open: true,
      mode,
    });
  };

  const closeReasonDialog = () => {
    setReasonDialogData({
      open: false,
      mode: null,
    });
  };

  const checkOpenProductionReportByUser = ({ onConfirm, actionText }) => {
    hideConfirmDialog();
    jobmonitorEasyService.checkOpenProductionReportByUser(userId).then((data) => {
      if (data.hasOpenedReport) {
        setConfirmDialogData({
          open: true,
          message: actionText,
          buttons: [
            {
              label: 'Igen',
              variant: 'contained',
              color: '#27ca37',
              onClick: () => {
                onConfirm();
              },
            },
          ],
        });
      } else {
        onConfirm();
      }
    });
  };

  const handleStartOperation = () => {
    const values = {
      userId: userId,
      createdBy: userId,
      id: data.productionoperationid,
    };
    jobmonitorEasyService.createProductionReport(values).then(() => {
      setRefetch(true);
      hideConfirmDialog();
    });
  };

  const handleDowntimeAction = (values) => {
    closeProductionReport({
      prodOperationId: data.productionoperationid,
      downtimeCode: quantityDialogData.reasonCode,
      userId,
      quantities: values,
      isDowntimeAction: true,
    });
  };

  const hasQuantity = (payload) => {
    const keys = Object.keys(payload || {});
    const numericKeys = keys.filter((k) => /^\d+$/.test(k));

    if (numericKeys.length > 0) {
      return numericKeys.every((k) => payload[k]?.quantity !== '');
    }

    return payload?.quantity !== '';
  };

  const handleFinishOperation = (values) => {
    if (!hasQuantity(values)) {
      addNotification({
        content: 'A mennyiség kitöltése kötelező!',
        type: NotificationType.ERROR,
      });
      return;
    }

    closeProductionReport({
      quantities: values,
      productionStatusId: 'COMPLETED',
      prodOperationId: data.productionoperationid,
      userId,
    });
  };

  const handleInterruptOperation = (values) => {
    if (!hasQuantity(values)) {
      addNotification({
        content: 'A mennyiség kitöltése kötelező!',
        type: NotificationType.ERROR,
      });
      return;
    }

    closeProductionReport({
      interruptionCode: quantityDialogData.reasonCode,
      productionStatusId: 'INTERRUPTED',
      quantities: values,
      prodOperationId: data.productionoperationid,
      userId,
    });
  };

  const openConfirmDialog = () => {
    const interimReport = reasons.interimReport;

    if (data.productionreportStartDate) {
      setConfirmDialogData({
        open: true,
        message: 'Mit szeretne csinálni?',
        buttons: [
          ...(!data.nonProductiveWork
            ? [
                {
                  label: 'Kész',
                  variant: 'contained',
                  color: '#27ca37',
                  onClick: () => {
                    hideConfirmDialog();
                    openQuantityDialog('finish');
                  },
                },
              ]
            : []),

          ...(params.JOBMONITOR_EASY_HIDE_INTERRUPT !== 'Y'
            ? [
                {
                  label: 'Megszakít',
                  variant: 'contained',
                  color: '#f59e0b',
                  onClick: () => {
                    hideConfirmDialog();
                    openReasonDialog(JmEasyActionMode.INTERRUPT);
                  },
                },
              ]
            : []),
          {
            label: data.downtimeCode ? 'Folytatás' : 'Állásidő',
            variant: 'contained',
            color: '#3464e9',
            onClick: () => {
              if (data.downtimeCode) {
                closeProductionReport({
                  prodOperationId: data.productionoperationid,
                  downtimeCode: null,
                  userId,
                  quantities: {},
                  isDowntimeAction: true,
                });
              } else {
                hideConfirmDialog();
                openReasonDialog(JmEasyActionMode.DOWNTIME);
              }
            },
          },
          ...(interimReport
            ? [
                {
                  label: interimReport.name,
                  variant: 'contained',
                  color: '#3bc3f5',
                  onClick: () => {
                    hideConfirmDialog();
                    openQuantityDialog(JmEasyActionMode.INTERRUPT, interimReport.code);
                  },
                },
              ]
            : []),
        ],
      });
    } else {
      setConfirmDialogData({
        open: true,
        message: 'Biztosan el szeretné indítani a gyártást?',
        buttons: [
          {
            label: 'Igen',
            variant: 'contained',
            color: '#27ca37',
            onClick: () => {
              checkOpenProductionReportByUser({
                onConfirm: handleStartOperation,
                actionText: 'Már van folyamatban lévő munkád. Megszakítod azt, hogy elindíthasd ezt?',
              });
            },
          },
        ],
      });
    }
  };

  const closeProductionReport = (data) => {
    jobmonitorEasyService.closeProductionReport(data).then(() => {
      hideConfirmDialog();
      closeQuantityDialog();
      setRefetch(true);
    });
  };

  const isInProgressWithoutReport = data.prodOpStatusId === 'INPROGRESS' && !data.productionreportStartDate;

  const statusColor = colorFinder(data.downtimeCode ? data.interruptedStatusColor : data.currentStatusColor);

  return (
    <Box
      sx={{
        borderRadius: 2,
        background: '#E4F0FF',
        p: 0.5,
        mb: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: isInProgressWithoutReport ? '#BCDBF7' : statusColor?.jobMonitorBgColor,
          borderRadius: 2,
          boxShadow:
            ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
          padding: 1,
          height: '100%',
          cursor: 'pointer',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#555',
          },

          '&:hover': {
            background: isInProgressWithoutReport ? '#96c8f5' : statusColor?.jobMonitorHoverColor,
          },
        }}
        onClick={() => openConfirmDialog()}
      >
        <Stack
          direction={{
            xs: 'column',
            lg: 'row',
          }}
          alignItems={{
            xs: 'stretch',
            lg: 'center',
          }}
          spacing={{
            xs: 1.5,
            lg: 2,
          }}
          mb={{
            xs: 1.5,
            lg: 3,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0} sx={{ order: { xs: 2, lg: 1 } }}>
            <Box
              width="fit-content"
              backgroundColor={isInProgressWithoutReport ? '#78afdf' : statusColor?.jobMonitorChipColor}
              p={0.5}
              px={1.5}
              borderRadius={2}
            >
              <Typography fontWeight="bold" fontSize={20}>
                {data.productionOperationStatus}
              </Typography>
            </Box>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                getAttachments(data.productionid);
              }}
            >
              <AttachmentIcon fontSize="large" />
            </IconButton>
          </Stack>
          <Box flex={1} minWidth={0} sx={{ order: { xs: 3, lg: 2 } }}>
            <InfoRow label="Művelet:" mb={0}>
              {data.operationName}
            </InfoRow>
          </Box>
          {data.productionreportStartDate && (
            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent={{
                xs: 'flex-start',
                sm: 'flex-end',
              }}
              gap={1}
              flexShrink={0}
              sx={{
                order: { xs: 1, lg: 3 },
                mb: { xs: '0.5rem !important', lg: '0rem !important' },
              }}
            >
              <Typography fontSize={20} lineHeight={1.2} sx={{ whiteSpace: 'nowrap', textWrap: 'nowrap', m: 0 }}>
                <Box component="span" fontWeight="bold">
                  Elindítva:{' '}
                </Box>
                <Box component="span">{dayjs(data.productionreportStartDate).format('YYYY-MM-DD HH:mm')}</Box>
              </Typography>

              <Typography
                color="blue"
                fontSize={20}
                lineHeight={1.2}
                sx={{
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {elapsedTime}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack
          direction={{
            xs: 'column',
            lg: 'row',
          }}
          alignItems={{
            xs: 'stretch',
            lg: 'center',
          }}
          spacing={1.5}
        >
          <Box flex={1} minWidth={0}>
            <InfoRow label="Megjegyzés:">{data.productionDescription}</InfoRow>
            <InfoRow label="Projekt:">{data.projectName}</InfoRow>
            <InfoRow label="Tervezett darabszám:">
              {data.productionQuantity} {data.articleQtyCode}
            </InfoRow>
            <InfoRow label="Cikknév:">{data.articleName}</InfoRow>
            <InfoRow label="Rájelentkezve:" valueSx={{ color: '#1968cf', fontWeight: 'bold' }}>
              {data.reportingBy}
            </InfoRow>
            <InfoRow label="Folyamat:" mb={1.5} />
          </Box>
        </Stack>
      </Box>
      <JmEasyReasonDialog
        open={reasonDialogData.open}
        handleClose={closeReasonDialog}
        data={reasons[reasonDialogData.mode] ?? []}
        mode={reasonDialogData.mode}
        handleSelect={(code) => {
          closeReasonDialog();
          openQuantityDialog(reasonDialogData.mode, code);
        }}
      />
      {!data?.stockAccounting ? (
        <JmEasyQuantityDialog
          open={quantityDialogData.open}
          handleClose={closeQuantityDialog}
          handleFinishOperation={handleFinishOperation}
          handleInterruptOperation={handleInterruptOperation}
          handleDowntimeAction={handleDowntimeAction}
          mode={quantityDialogData.mode}
          autoCompleteOptions={autoCompleteOptions}
          helperData={helperData}
          data={data}
        />
      ) : (
        <ProductionReceiptDialog
          open={quantityDialogData.open}
          onClose={closeQuantityDialog}
          handleFinishOperation={handleFinishOperation}
          handleInterruptOperation={handleInterruptOperation}
          handleDowntimeAction={handleDowntimeAction}
          mode={quantityDialogData.mode}
          onSave={closeQuantityDialog}
          autocompleteData={helperData}
          cardData={data}
          autoCompleteOptions={autoCompleteOptions}
        />
      )}
    </Box>
  );
};

export default JmEasyJobmonitorCard;
